import React, { useState, useEffect, useRef } from 'react';
import { MdAdd } from 'react-icons/md';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import TmMasterTable from '../ReusableComponents/TmMasterTable';
import AntdModalComp from '../ReusableComponents/AntdModalComp';
import TmFilter from '../ReusableComponents/TmFilter';
import { DeletePopup } from '../ReusableComponents/DeletePopup';

import { QualificationApi } from '../../Api/QualificationApi';
import AddQualification from './AddQualification';
import { QualificationHeader } from '../../constant/tableHeader';
import { toastError, toastSuccess, toastWarn } from '../Toast/ToastComponent';
import { getSurveyCategory } from '../../store/reducers/actionPermission'; // reuse same permission
import { ACTIVE_STATUS } from '../../constant/constant';
import SearchInput from '../ReusableComponents/SearchInput';

const defaultModalData = { title: '', id: null, status: true };

const filterArray = [
  {
    options: ACTIVE_STATUS,
    optionFilterProp: 'label',
    placeholder: 'Status',
    order: 1,
    col: 2,
    defaultValue: true,
    controlName: 'status',
    allowClear: true,
    input: 'select',
  },
  {
    order: 1,
    col: 'auto',
    className: 'ms-auto pe-0',
    input: 'button',
    type: 'dashed',
    htmlType: 'button',
    text: 'Reset',
  },
];

const QualificationComp = () => {
  const navigate = useNavigate();
  const debounceTimeoutRef = useRef(null);

  const [modal, setModal] = useState(false);
  const [qualificationData, setQualificationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalHeader, setModalHeader] = useState(null);
  const [modalData, setModalData] = useState(defaultModalData);

  const [filterArr, setFilterArr] = useState({
    search: '',
    status: true,
    page: 1,
    limit: 100000,
  });

  const hasFetched = useRef(false);

  const permission = useSelector(getSurveyCategory);

  const [buttonPermission, setButtonPermissions] = useState({
    add: undefined,
    edit: undefined,
    delete: undefined,
  });

  useEffect(() => {
    if (hasFetched.current) return;

    hasFetched.current = true;
    getQualificationList();

    if (permission) {
      handleButtonPermission();
    }
  }, [permission]);

  const handleButtonPermission = () => {
    if (!permission?.checked) {
      navigate('/');
    }

    let newObj = {};
    for (const element of permission?.actions) {
      const keyName = element?.statename?.toLowerCase();
      newObj[keyName] = element?.checked;
    }

    setButtonPermissions((prev) => ({
      ...prev,
      ...newObj,
    }));
  };

  const handleFilterChange = (obj) => {
    const newFilter = { ...filterArr, ...obj, page: 1 };
    setFilterArr(newFilter);
    getQualificationList(newFilter);
  };

  const getQualificationList = async (filterObj = filterArr) => {
    setLoading(true);
    try {
      const response = await QualificationApi.get(filterObj);

      if (response?.data?.success) {
        const list = response.data.result || [];

        const mappedData = list.map((item) => ({
          id: item.id,
          qualification_name: item.qualification_name,
          status: Boolean(item.status),
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          serialNo: item.serialNo,
        }));

        setQualificationData(mappedData);
      } else {
        setQualificationData([]);
        toastError(response?.data?.message);
      }
    } catch (error) {
      setQualificationData([]);
      toastError(
        error?.response?.data?.message ||
          error?.message ||
          'Something went wrong'
      );
    }
    setLoading(false);
  };

  const toggleModal = (type, value) => {
    if (type === 'Close') {
      setModal(false);
      setModalHeader(null);
      setModalData(defaultModalData);
      return;
    }

    if (value && (type === 'Edit' || type === 'View')) {
      setModalData({
        title: value.qualification_name,
        id: value.id,
        status: Boolean(value.status),
      });
    } else {
      setModalData(defaultModalData);
    }

    setModalHeader(<span>{`${type} Qualification`}</span>);
    setModal(true);
  };

  const callDelete = (id) => {
    DeletePopup(async (canDelete) => {
      if (canDelete) {
        try {
          const response = await QualificationApi.delete({ id });

          if (response.data.success) {
            toastSuccess(response.data.message);
            getQualificationList(filterArr);
          } else {
            toastWarn(response.data.message);
          }
        } catch (error) {
          toastError(
            error?.response?.data?.message ||
              error?.message ||
              'Something went wrong'
          );
        }
      }
    });
  };

  const handleSearchChange = (value) => {
    const newFilter = { ...filterArr, search: value, page: 1 };
    setFilterArr(newFilter);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      getQualificationList(newFilter);
    }, 500);
  };

  const handleReset = () => {
    const resetFilter = {
      search: '',
      status: true,
      page: 1,
      limit: 10,
    };
    setFilterArr(resetFilter);
    getQualificationList(resetFilter);
  };

  const updatedFilterArray = filterArray.map((item) =>
    item.input === 'button' && item.text === 'Reset'
      ? { ...item, onClick: handleReset }
      : item
  );

  return (
    <>
      <div className="d-md-flex">
        <h5>Qualification</h5>

        <div className="ms-md-auto d-flex">
          <div className="me-2">
            <SearchInput
              placeholder="Search"
              value={filterArr.search}
              onSearchChange={handleSearchChange}
            />
          </div>

          {buttonPermission?.add && (
            <Button
              type="primary"
              onClick={() => toggleModal('Add')}
              icon={<MdAdd size={20} />}
            />
          )}
        </div>
      </div>

      <div className="mt-2">
        <TmFilter
          filterArray={updatedFilterArray}
          handleFilterChange={handleFilterChange}
          initialValues={{ status: true }}
        />
      </div>

      <div className="mt-3">
        <TmMasterTable
          buttonPermission={buttonPermission}
          rows={qualificationData}
          columns={QualificationHeader}
          custom={['checkbox', 'action']}
          toggleModal={toggleModal}
          handleDelete={callDelete}
          loading={loading}
          noDataText="No Data Found"
        />
      </div>

      <AntdModalComp
        width="30%"
        toggleModal={() => toggleModal('Close')}
        modalOpen={modal}
        modalHeader={modalHeader}
        modalBody={
          <AddQualification
            modalHeader={modalHeader}
            getQualificationList={getQualificationList}
            setModal={setModal}
            modal={modal}
            modalData={modalData}
            filterArr={filterArr}
          />
        }
      />
    </>
  );
};

export default QualificationComp;